import React from 'react';
import Select, { components, createFilter, SingleValueProps, MultiValueProps } from 'react-select';
import { Config } from 'react-select/src/filters';
import { useTranslation } from 'react-i18next';
import FormGroup from 'features/shared/forms/components/FormGroup';
import ContractDropdownDTO from 'features/contracts/dtos/ContractDropdownDTO';
import CommodityIcon from 'features/shared/components/icons/CommodityIcon';
import ContractStatusLabel from 'features/contracts/components/ContractStatusLabel';
import reactSelectStyles from 'features/shared/components/reactSelect/reactselectcustom.module.scss';
import { Nullable } from 'features/shared/types/nullable';

export type ContractSelectUnifiedValue = ContractDropdownDTO | ContractDropdownDTO[] | null;

export interface ContractSelectUnifiedProps {
  contracts: ContractDropdownDTO[];
  onChange: (contract: Nullable<ContractDropdownDTO> | Nullable<ContractDropdownDTO[]>) => void;
  name: string;
  label?: string;
  placeholder?: string;
  errors?: Record<string, string>;
  value?: ContractSelectUnifiedValue;
  type?: 'Contract' | 'PlaceOfDelivery';
  isMulti?: boolean;
  closeMenuOnSelect?: boolean;
  noOptionsMessage?: string;
}

const ContractSelectUnified: React.FC<ContractSelectUnifiedProps> = ({
  contracts,
  onChange,
  name,
  label,
  placeholder,
  errors,
  value,
  type = 'Contract',
  isMulti = false,
  closeMenuOnSelect,
  noOptionsMessage
}) => { 
  const { t } = useTranslation('contracts\\contracts');

  // const multiMode = !!isMulti;

  const filterConfig: Config = {
    stringify: (obj: any) => {
      const { contractNumber, placeOfDelivery, placeOfDeliveryAddress } = obj.data as ContractDropdownDTO;
      return `${contractNumber} ${placeOfDelivery} ${placeOfDeliveryAddress}`;
    }
  };

  const selectedValue: ContractSelectUnifiedValue = (() => {
    if (value === undefined) {
      return isMulti ? [] : null;
    }
    return value as any;
  })();

  const getSelectedItemText = (item: ContractDropdownDTO): string => {
    switch (type) {
      case 'Contract':
        return item.contractNumber || '';
      case 'PlaceOfDelivery':
        return item.placeOfDelivery || '';
      default:
        return '';
    }
  };

  const renderOption = (contract: ContractDropdownDTO) => (
    <div className="d-flex align-items-center">
      <CommodityIcon commodityDetail={contract.commodityDetail!} includeColor size="lg" />
      <div className="pl-2" style={{ minWidth: 0 }}>
        <div className="text-truncate">
          <span>{contract.placeOfDelivery}</span>
          <span className="mx-2 text-muted">|</span>
          <span className="text-muted">{contract.contractNumber}</span>
        </div>
        <div className="text-truncate mt-n1">
          {contract.contractStatus && (
            <ContractStatusLabel status={contract.contractStatus} asText className="mr-2" />
          )}
          <span className="small text-muted">{contract.placeOfDeliveryAddress}</span>
        </div>
      </div>
    </div>
  );

  const renderSingleValue = (svProps: SingleValueProps<ContractDropdownDTO>) => (
    <components.SingleValue {...svProps}>{getSelectedItemText(svProps.data)}</components.SingleValue>
  );

  const renderMultiValue = (mvProps: MultiValueProps<ContractDropdownDTO>) => (
    <components.MultiValue {...mvProps}>
      <span>{getSelectedItemText(mvProps.data)}</span>
    </components.MultiValue>
  );

  const handleChange = (selection: any) => {
    let payload: ContractSelectUnifiedValue;
    if (Array.isArray(selection)) {
      payload = selection as ContractDropdownDTO[];
    } else {
      payload = (selection as ContractDropdownDTO) ?? null;
    }
    onChange(payload as any);
  };

  return (
    <FormGroup
      labelText={label ?? t(`contractSelect.${type}.Label`)}
      validationError={errors ? errors[name] : undefined}
      inputId={name}
    >
      <Select
        name={name}
        isMulti={isMulti}
        options={contracts}
        value={selectedValue as any}
        onChange={handleChange as any}
        getOptionLabel={(c) => c.contractNumber!}
        getOptionValue={(c) => String(c.contractId)}
        formatOptionLabel={(c) => renderOption(c)}
        components={{ SingleValue: renderSingleValue, MultiValue: renderMultiValue }}
        filterOption={createFilter(filterConfig)}
        captureMenuScroll={false}
        className={reactSelectStyles.customSelect}
        classNamePrefix="customSelect"
        placeholder={placeholder ?? t(`contractSelect.${type}.Placeholder`)}
        noOptionsMessage={() =>  noOptionsMessage ?? t(`contractSelect.${type}.NoOptions`)}
        closeMenuOnSelect={closeMenuOnSelect ?? !isMulti}
        hideSelectedOptions={false}
      />
    </FormGroup>
  );
};

export default ContractSelectUnified;
